import { lmsGet, lmsPost } from "../admin/lmsApiClient";
import {
  LMS_API,
  resolveOrgId,
  resolveLmsShortName,
  setPasswordBaseUrl,
} from "./_learnerConfig";

// ─────────────────────────────────────────────────────────────────────────────
// ESHS LMS — learner public/auth API wrappers.
//
// All calls go through the single shared LMS client (`lmsApiClient`), which
// injects the ecauthtoken/ecsecuretoken headers and captures refreshed tokens
// off every response (sliding renewal). Public endpoints don't need the tokens,
// but routing them through the same client means the ONE client serves both the
// admin surface and the learner portal — never two clients.
//
// Every response is the { status, data, message } envelope. Callers treat
// app-status 204 as a SOFT validation error (render `message` inline), 200 as
// success, and rely on the client for hard HTTP errors (toast / 401 / 429).
// Registration uses lmsShortName as its explicit tenant selector. orgId remains
// on the other public APIs whose contracts still require it.
// ─────────────────────────────────────────────────────────────────────────────

/* ───────────── Registration: email-first, verify, submit ───────────── */

// Step 1 — send a one-time code to the learner's email (also used for resend;
// the server enforces a 60s cooldown and may answer 204 "Please wait N seconds").
export const learnerPortalInfoApi = ({ lmsShortName } = {}) =>
  lmsGet(
    `${LMS_API}/public/portal?lmsShortName=${encodeURIComponent(
      lmsShortName || resolveLmsShortName()
    )}`
  );

export const learnerSendOtpApi = ({ lmsShortName, email }) =>
  lmsPost(`${LMS_API}/public/register/send-otp`, {
    lmsShortName: lmsShortName || resolveLmsShortName(),
    email,
  });

// Step 2 — verify the 6-digit code. Success → data.verified === true, opening a
// 30-minute window to complete registration.
export const learnerVerifyOtpApi = ({ lmsShortName, email, otp }) =>
  lmsPost(`${LMS_API}/public/register/verify-otp`, {
    lmsShortName: lmsShortName || resolveLmsShortName(),
    email,
    otp,
  });

// Step 3 dropdown data — regions with nested districts (district select is
// dependent on the chosen region's nested array).
export const learnerRegionsApi = ({ lmsShortName } = {}) =>
  lmsGet(
    `${LMS_API}/public/config/regions?lmsShortName=${encodeURIComponent(
      lmsShortName || resolveLmsShortName()
    )}`
  );

// Step 3 — submit registration using the same explicit tenant selector as the
// OTP steps. `orgId` is deliberately discarded if an older caller supplies it.
export const learnerRegisterApi = (fields = {}) => {
  const registrationFields = { ...fields };
  const lmsShortName = registrationFields.lmsShortName;
  delete registrationFields.orgId;
  delete registrationFields.lmsShortName;
  return lmsPost(`${LMS_API}/public/register`, {
    lmsShortName: lmsShortName || resolveLmsShortName(),
    ...registrationFields,
    setPasswordBaseUrl: setPasswordBaseUrl(),
  });
};

/* ───────────── Passwords ───────────── */

// Set OR reset a password from an emailed token (activation + forgot both land
// here). Password policy is mirrored client-side before this is called.
export const learnerResetPasswordApi = ({ email, token, password }) =>
  lmsPost(`${LMS_API}/public/reset-password`, {
    lmsShortName: resolveLmsShortName(),
    orgId: resolveOrgId(),
    email,
    token,
    password,
  });

// Forgot password — always answered generically (no account enumeration).
export const learnerForgotPasswordApi = ({ email }) =>
  lmsPost(`${LMS_API}/public/forgot-password`, {
    lmsShortName: resolveLmsShortName(),
    orgId: resolveOrgId(),
    email,
  });

/* ───────────── Login ───────────── */

// Login — success returns data.tokens { ecAuthToken, ecSecureToken } and sets
// cookies; the caller persists the tokens (see learnerAuth.persistTokens).
export const learnerLoginApi = ({ email, password }) =>
  lmsPost(`${LMS_API}/public/login`, {
    lmsShortName: resolveLmsShortName(),
    orgId: resolveOrgId(),
    email,
    password,
  });

/* ───────────── Authenticated (used by the route guard's silent slide) ───────────── */

// GET /me — cheap authenticated probe. Also refreshes sliding tokens via the
// client's response interceptor, and validates a stored session on guarded routes.
export const learnerMeApi = () => lmsGet(`${LMS_API}/me`);

/* ───────────── Profile ───────────── */

// Update profile fields (serviceArea follows serviceAudience server-side).
export const learnerUpdateMeApi = (fields) => lmsPost(`${LMS_API}/me`, fields);

export const learnerChangePasswordApi = ({ currentPassword, newPassword }) =>
  lmsPost(`${LMS_API}/me/change-password`, { currentPassword, newPassword });

/* ───────────── Catalog & enrolment ───────────── */

// Published courses + the learner's enrolment status per course.
export const learnerCoursesApi = (query = "") =>
  lmsGet(`${LMS_API}/courses${query}`);

// Course detail: persona intro asset, ordered published modules (locked flags,
// sections w/ assets + quiz summaries, completion + support assets), per-asset
// progress, course quiz and the learner's enrolment. Signed URLs only for
// enrolled learners on unlocked modules.
export const learnerCourseDetailApi = (courseId) =>
  lmsGet(`${LMS_API}/courses/${courseId}`);

// Idempotent enrol; `retake: true` starts a re-certification cycle.
export const learnerEnrolApi = (courseId, retake = false) =>
  lmsPost(`${LMS_API}/courses/enrol`, { courseId, ...(retake ? { retake: true } : {}) });

export const learnerEnrolmentsApi = (query = "") =>
  lmsGet(`${LMS_API}/enrolments${query}`);

/* ───────────── Progress ───────────── */

// Report one asset's progress. assetLocation ∈ course_intro | module_intro |
// section | completion | support_service. Video watched-seconds only move
// forward server-side; pdf/image/external/rich_text complete on first view.
export const learnerAssetProgressApi = (payload) =>
  lmsPost(`${LMS_API}/progress/asset`, payload);

/* ───────────── Quiz attempts ───────────── */

// `courseId` is the route course identifier and may be either a Mongo id or a
// public course slug. The backend resolves it inside the authenticated tenant.
export const learnerQuizStartApi = (quizId, enrolmentId, courseId) =>
  lmsPost(`${LMS_API}/quiz/start`, {
    quizId,
    ...(enrolmentId ? { enrolmentId } : {}),
    ...(courseId ? { courseId } : {}),
  });

export const learnerQuizDraftApi = (attemptId, answers) =>
  lmsPost(`${LMS_API}/quiz/draft`, { attemptId, answers });

export const learnerQuizSubmitApi = (attemptId, answers, enrolmentId) =>
  lmsPost(`${LMS_API}/quiz/submit`, {
    attemptId,
    answers,
    ...(enrolmentId ? { enrolmentId } : {}),
  });

export const learnerCertificatesApi = () => lmsGet(`${LMS_API}/certificates`);
